import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { SearchableSelect } from "../../components/ui/searchable-select";
import SettingsSection from "./SettingsSection";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const TIMEZONES = [
  { value: "Asia/Kolkata", label: "India — IST (GMT+5:30)" },
  { value: "UTC", label: "UTC (GMT+0:00)" },
  { value: "Asia/Dubai", label: "Dubai (GMT+4:00)" },
  { value: "Asia/Riyadh", label: "Riyadh (GMT+3:00)" },
  { value: "Asia/Karachi", label: "Karachi (GMT+5:00)" },
  { value: "Asia/Kathmandu", label: "Kathmandu (GMT+5:45)" },
  { value: "Asia/Colombo", label: "Colombo (GMT+5:30)" },
  { value: "Asia/Dhaka", label: "Dhaka (GMT+6:00)" },
  { value: "Asia/Bangkok", label: "Bangkok / Jakarta (GMT+7:00)" },
  { value: "Asia/Singapore", label: "Singapore (GMT+8:00)" },
  { value: "Asia/Hong_Kong", label: "Hong Kong (GMT+8:00)" },
  { value: "Asia/Manila", label: "Manila (GMT+8:00)" },
  { value: "Asia/Tokyo", label: "Tokyo (GMT+9:00)" },
  { value: "Australia/Sydney", label: "Sydney (GMT+10/11)" },
  { value: "Pacific/Auckland", label: "Auckland (GMT+12/13)" },
  { value: "Europe/London", label: "London (GMT+0/1)" },
  { value: "Europe/Berlin", label: "Berlin / Paris (GMT+1/2)" },
  { value: "Europe/Warsaw", label: "Warsaw (GMT+1/2)" },
  { value: "Africa/Johannesburg", label: "Johannesburg (GMT+2:00)" },
  { value: "Africa/Nairobi", label: "Nairobi (GMT+3:00)" },
  { value: "America/Sao_Paulo", label: "São Paulo (GMT-3:00)" },
  { value: "America/New_York", label: "New York (GMT-5/-4)" },
  { value: "America/Toronto", label: "Toronto (GMT-5/-4)" },
  { value: "America/Chicago", label: "Chicago (GMT-6/-5)" },
  { value: "America/Los_Angeles", label: "Los Angeles (GMT-8/-7)" },
];

export default function SlaPanel() {
  // SLA config state
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  // Ticket statuses state
  const [statuses, setStatuses] = useState([]);

  // Priorities state
  const [priorities, setPriorities] = useState([]);

  // Load SLA config
  useEffect(() => {
    api
      .get("/company/sla")
      .then((r) => {
        const cfg = r.data;
        if (!TIMEZONES.some((t) => t.value === cfg.timezone)) cfg.timezone = "Asia/Kolkata";
        setConfig(cfg);
      })
      .catch(() => toast.error("Failed to load SLA config"));
  }, []);

  // Load ticket statuses
  useEffect(() => {
    api
      .get("/ticket-statuses")
      .then((r) => setStatuses(r.data))
      .catch(() => toast.error("Failed to load ticket statuses"));
  }, []);

  // Load priorities
  useEffect(() => {
    api
      .get("/masters/priority")
      .then((r) => setPriorities(r.data))
      .catch(() => toast.error("Failed to load priorities"));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/company/sla", {
        enabled: config.enabled,
        mode: config.mode,
        timezone: config.timezone,
        always_on: config.always_on,
        work_days: config.work_days,
        work_start: config.work_start,
        work_end: config.work_end,
        auto_reply_timeline: config.auto_reply_timeline,
        notify_client_on_breach: config.notify_client_on_breach,
        notify_staff_on_approaching: config.notify_staff_on_approaching,
        approaching_lead_minutes: config.approaching_lead_minutes,
      });
      setConfig(data);
      toast.success("SLA settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save SLA settings");
    } finally {
      setSaving(false);
    }
  };

  const toggleAutomation = async (key) => {
    const nextVal = !config[key];
    const prevConfig = config;
    setConfig((prev) => ({ ...prev, [key]: nextVal }));
    try {
      const { data } = await api.patch("/company/sla", { [key]: nextVal });
      setConfig(data);
    } catch (e) {
      setConfig(prevConfig);
      toast.error(e.response?.data?.detail || "Failed to update setting");
    }
  };

  const saveLeadMinutes = async (minutes) => {
    const parsed = parseInt(minutes, 10);
    if (!isFinite(parsed) || parsed <= 0) return;
    try {
      const { data } = await api.patch("/company/sla", { approaching_lead_minutes: parsed });
      setConfig(data);
      toast.success("Warning lead time updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update lead time");
    }
  };

  const toggleDay = (day) => {
    setConfig((prev) => {
      const days = prev.work_days.includes(day)
        ? prev.work_days.filter((d) => d !== day)
        : [...prev.work_days, day].sort((a, b) => a - b);
      return { ...prev, work_days: days };
    });
  };

  const handleModeChange = async (newMode) => {
    const prevMode = config.mode;
    setConfig((prev) => ({ ...prev, mode: newMode }));
    try {
      const { data } = await api.patch("/company/sla", { mode: newMode });
      setConfig(data);
      toast.success("SLA mode updated");
    } catch (e) {
      setConfig((prev) => ({ ...prev, mode: prevMode }));
      toast.error(e.response?.data?.detail || "Failed to update SLA mode");
    }
  };

  const handlePriorityTarget = async (priority, rawValue) => {
    const trimmed = rawValue.trim();
    if (trimmed === "") {
      // blank = no change; do not send a request
      return;
    }
    const hours = parseFloat(trimmed);
    if (!isFinite(hours) || hours <= 0) {
      toast.error("Target must be a positive number");
      return;
    }
    const sla_minutes = Math.round(hours * 60);
    try {
      const { data } = await api.patch(`/masters/priority/${priority.id}`, { sla_minutes });
      setPriorities((prev) => prev.map((p) => (p.id === priority.id ? data : p)));
      toast.success(`Target updated for "${priority.label}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update target");
    }
  };

  const handleStatusTarget = async (status, rawValue) => {
    const trimmed = rawValue.trim();
    if (trimmed === "") {
      // blank = no change; do not send a request
      return;
    }
    const hours = parseFloat(trimmed);
    if (!isFinite(hours) || hours <= 0) {
      toast.error("Target must be a positive number");
      return;
    }
    const sla_minutes = Math.round(hours * 60);
    try {
      const { data } = await api.patch(`/ticket-statuses/${status.id}`, { sla_minutes });
      setStatuses((prev) => prev.map((s) => (s.id === status.id ? data : s)));
      toast.success(`Target updated for "${status.label}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update target");
    }
  };

  if (!config) {
    return (
      <SettingsSection title="SLA" description="Service Level Agreement settings for your workspace.">
        <div className="text-sm text-slate-400">Loading…</div>
      </SettingsSection>
    );
  }

  const enabledStatuses = statuses.filter((s) => s.enabled);

  return (
    <SettingsSection
      title="SLA"
      description="Configure SLA tracking, working hours, and per-status resolution targets."
    >
      {/* ── SLA Config Card ── */}
      <div className="surface-card p-5 space-y-5">
        <div className="text-sm font-semibold text-slate-700">Global SLA settings</div>

        {/* Enable SLA */}
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-medium text-slate-800">Enable SLA</div>
            <div className="text-xs text-slate-400 mt-0.5">
              Track resolution time against targets on every ticket.
            </div>
          </div>
          <Switch
            checked={!!config.enabled}
            onCheckedChange={(v) => setConfig((prev) => ({ ...prev, enabled: v }))}
            data-testid="sla-enable"
          />
        </div>

        {/* Mode selector (shown when SLA is enabled) */}
        {config.enabled && (
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-medium text-slate-800">SLA mode</div>
              <div className="text-xs text-slate-400 mt-0.5">
                Choose how SLA targets are applied — per status or per priority.
              </div>
            </div>
            <SearchableSelect
              value={config.mode || "status"}
              onValueChange={handleModeChange}
              options={[
                { value: "status", label: "Status-wise" },
                { value: "priority", label: "Priority-wise" },
              ]}
              className="w-40"
              testId="sla-mode"
            />
          </div>
        )}

        {/* 24×7 toggle */}
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-medium text-slate-800">24 × 7</div>
            <div className="text-xs text-slate-400 mt-0.5">
              SLA clocks run around the clock, every day of the week.
            </div>
          </div>
          <Switch
            checked={!!config.always_on}
            onCheckedChange={(v) => setConfig((prev) => ({ ...prev, always_on: v }))}
            data-testid="sla-always-on"
          />
        </div>

        {/* Working hours (shown only when not 24×7) */}
        {!config.always_on && (
          <div className="space-y-4 pt-2 border-t border-slate-100">
            {/* Timezone */}
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Timezone</label>
              <SearchableSelect
                value={TIMEZONES.some((t) => t.value === config.timezone) ? config.timezone : "Asia/Kolkata"}
                onValueChange={(v) => setConfig((prev) => ({ ...prev, timezone: v }))}
                options={TIMEZONES}
                testId="sla-timezone"
              />
            </div>

            {/* Working days */}
            <div>
              <label className="text-xs text-slate-500 mb-2 block">Working days</label>
              <div className="flex flex-wrap gap-2">
                {DAY_LABELS.map((label, idx) => {
                  const active = config.work_days?.includes(idx);
                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => toggleDay(idx)}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                        active
                          ? "bg-blue-600 text-white border-blue-600"
                          : "bg-white text-slate-600 border-slate-200 hover:border-blue-400"
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Work hours */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Start time</label>
                <input
                  type="time"
                  className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  value={config.work_start || ""}
                  onChange={(e) =>
                    setConfig((prev) => ({ ...prev, work_start: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">End time</label>
                <input
                  type="time"
                  className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  value={config.work_end || ""}
                  onChange={(e) =>
                    setConfig((prev) => ({ ...prev, work_end: e.target.value }))
                  }
                />
              </div>
            </div>
          </div>
        )}

        <Button onClick={handleSave} disabled={saving} data-testid="sla-save">
          {saving ? "Saving…" : "Save SLA settings"}
        </Button>
      </div>

      {/* ── SLA Automation Card ── */}
      {config.enabled && (
        <div className="surface-card p-5 space-y-5">
          <div className="text-sm font-semibold text-slate-700">SLA automation</div>

          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-medium text-slate-800">Email the client a resolution timeline</div>
              <div className="text-xs text-slate-400 mt-0.5">
                Include the expected resolution date in the "we received your ticket" email.
              </div>
            </div>
            <Switch
              checked={!!config.auto_reply_timeline}
              onCheckedChange={() => toggleAutomation("auto_reply_timeline")}
              data-testid="sla-auto-reply-timeline"
            />
          </div>

          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-medium text-slate-800">Message the client automatically when SLA is missed</div>
              <div className="text-xs text-slate-400 mt-0.5">
                Reassures the client the team is actively working on it, as soon as the deadline passes.
              </div>
            </div>
            <Switch
              checked={!!config.notify_client_on_breach}
              onCheckedChange={() => toggleAutomation("notify_client_on_breach")}
              data-testid="sla-notify-client-breach"
            />
          </div>

          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-medium text-slate-800">Warn staff before SLA is missed</div>
              <div className="text-xs text-slate-400 mt-0.5">
                Emails super admins, project managers, and the assignee when a ticket is close to breaching its SLA.
              </div>
            </div>
            <Switch
              checked={!!config.notify_staff_on_approaching}
              onCheckedChange={() => toggleAutomation("notify_staff_on_approaching")}
              data-testid="sla-notify-staff-approaching"
            />
          </div>

          {config.notify_staff_on_approaching && (
            <div className="flex items-center justify-between gap-3 pt-2 border-t border-slate-100">
              <div>
                <div className="text-sm font-medium text-slate-800">Warning lead time</div>
                <div className="text-xs text-slate-400 mt-0.5">How many minutes before the deadline to send the warning.</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <input
                  type="number"
                  min="1"
                  step="1"
                  defaultValue={config.approaching_lead_minutes}
                  onBlur={(e) => saveLeadMinutes(e.target.value)}
                  data-testid="sla-approaching-lead-minutes"
                  className="w-24 h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                />
                <span className="text-xs text-slate-400">min</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Per-Target Card (mode-aware) ── */}
      {config.mode === "priority" ? (
        <div className="surface-card overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100">
            <div className="text-sm font-semibold text-slate-700">Per-priority resolution targets</div>
            <div className="text-xs text-slate-400 mt-0.5">
              Set a default target (in hours) for each priority. Leave blank to keep the existing target unchanged.
            </div>
          </div>
          <div className="divide-y divide-slate-100">
            {priorities.length === 0 && (
              <div className="px-4 py-6 text-xs text-slate-400 text-center">No priorities found.</div>
            )}
            {priorities.map((p) => (
              <PriorityTargetRow key={p.id} priority={p} onSave={handlePriorityTarget} />
            ))}
          </div>
        </div>
      ) : (
        <div className="surface-card overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100">
            <div className="text-sm font-semibold text-slate-700">Per-status resolution targets</div>
            <div className="text-xs text-slate-400 mt-0.5">
              Set a default target (in hours) for each enabled status. Leave blank to keep the existing target unchanged.
            </div>
          </div>
          <div className="divide-y divide-slate-100">
            {enabledStatuses.length === 0 && (
              <div className="px-4 py-6 text-xs text-slate-400 text-center">No enabled statuses.</div>
            )}
            {enabledStatuses.map((s) => (
              <StatusTargetRow key={s.id} status={s} onSave={handleStatusTarget} />
            ))}
          </div>
        </div>
      )}
    </SettingsSection>
  );
}

function StatusTargetRow({ status, onSave }) {
  const initialValue =
    status.sla_minutes != null ? String(status.sla_minutes / 60) : "";
  const [value, setValue] = useState(initialValue);
  const [saving, setSaving] = useState(false);

  const handleBlur = async () => {
    if (value === initialValue) return; // no change
    setSaving(true);
    await onSave(status, value);
    setSaving(false);
  };

  return (
    <div className="px-5 py-3 flex items-center gap-4">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-800">{status.label}</div>
        <div className="text-xs text-slate-400">{status.key}</div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <input
          type="number"
          step="0.5"
          min="0"
          placeholder="hours"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={handleBlur}
          disabled={saving}
          data-testid={`sla-status-target-${status.key}`}
          className="w-24 h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
        />
        <span className="text-xs text-slate-400">hrs</span>
      </div>
    </div>
  );
}

function PriorityTargetRow({ priority, onSave }) {
  const initialValue =
    priority.sla_minutes != null ? String(priority.sla_minutes / 60) : "";
  const [value, setValue] = useState(initialValue);
  const [saving, setSaving] = useState(false);

  const handleBlur = async () => {
    if (value === initialValue) return; // no change
    setSaving(true);
    await onSave(priority, value);
    setSaving(false);
  };

  return (
    <div className="px-5 py-3 flex items-center gap-4">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-800">{priority.label}</div>
        <div className="text-xs text-slate-400">{priority.value}</div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <input
          type="number"
          step="0.5"
          min="0"
          placeholder="hours"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={handleBlur}
          disabled={saving}
          data-testid={`sla-priority-target-${priority.value}`}
          className="w-24 h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
        />
        <span className="text-xs text-slate-400">hrs</span>
      </div>
    </div>
  );
}
